import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Star, Sparkles, ShieldCheck, Truck, Heart, Minus, Plus, MessageCircle, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { fetchProduct, fetchProducts } from '@/lib/api';
import { useApp } from '@/state/AppContext';
import { inr, classNames } from '@/lib/format';
import { ProductCard } from '@/components/ProductCard';
import { Footer } from '@/components/Footer';
import { AddToCartButton } from '@/components/AddToCartButton';
import { imgSrc } from '@/lib/img';

const TONE = {
  chocopops: { btn: 'bg-[var(--bb-cyan)]', btnH: 'hover:bg-[var(--bb-cyan-deep)]', text: 'text-[var(--bb-cyan)]' },
  chocodigits: { btn: 'bg-[var(--bb-pink)]', btnH: 'hover:bg-[var(--bb-pink-deep)]', text: 'text-[var(--bb-pink)]' },
  chocosnaps: { btn: 'bg-[var(--bb-yellow)]', btnH: 'hover:bg-[#e6ac00]', text: 'text-[var(--bb-darkblue)]' },
};

const ProductDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, pushRecent, config } = useApp();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [activeImage, setActiveImage] = useState(null); // image url shown in main viewer
  const relatedRef = useRef(null);

  const scrollToMore = () => {
    // Defer until after success-state visible (~900ms), then smooth-scroll to related section
    setTimeout(() => {
      const el = relatedRef.current;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: window.scrollY + 600, behavior: 'smooth' });
      }
    }, 900);
  };

  useEffect(() => {
    setLoading(true);
    fetchProduct(slug).then((p) => {
      setProduct(p);
      setActiveImage(p.image);
      pushRecent(p);
    }).finally(() => setLoading(false));
  }, [slug, pushRecent]);

  useEffect(() => {
    if (!product) return;
    fetchProducts({}).then((xs) => setRelated(xs.filter((x) => x.is_pack && x.slug !== product.slug).slice(0, 4)));
  }, [product]);

  // Gallery thumbnails: main image + extra product images (pack_options images are not shown here —
  // they have their own chip-driven preview in the "What's inside" section). MUST be before any early return.
  const thumbs = useMemo(() => {
    if (!product) return [];
    const list = [product.image, ...(product.images || [])];
    return Array.from(new Set(list.filter(Boolean))).slice(0, 12);
  }, [product]);

  if (loading || !product) {
    return <div className="max-w-6xl mx-auto px-4 md:px-6 py-8"><div className="animate-pulse h-[60vh] rounded-2xl bg-white border border-[var(--bb-darkblue)]/10" /></div>;
  }

  const tone = TONE[product.category] || TONE.chocopops;
  const isPack = !!product.is_pack;
  const isMono = !!product.is_mono;
  const allowCust = !!product.allow_customisation;
  const showCustomiseCTA = (isPack && !isMono) || allowCust;
  const discount = product.compare_at_price && product.compare_at_price > product.price
    ? Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100) : 0;

  const addPlain = () => {
    addToCart({
      product_id: product.id, product_slug: product.slug, name: product.name,
      image: product.image, price: product.price, qty, variant: product.variant,
      customization: null,
      pack: isMono ? { pack_size: product.pack_size, pack_unit: product.pack_unit, selections: product.pack_options.reduce((a, o) => { a[o.id] = o.default_qty || product.pack_size; return a; }, {}) } : null,
    });
    toast.success(`${product.name} added to cart`, { description: `Quantity: ${qty}` });
    scrollToMore();
  };
  const buyNow = () => { addPlain(); navigate('/checkout'); };
  const goCustomise = () => navigate(`/product/${product.slug}/customize`);

  return (
    <div className="pb-32 md:pb-16" data-testid="product-detail-page">
      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-3">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm font-nunito-bold text-[var(--bb-darkgrey)] hover:text-[var(--bb-darkblue)]" data-testid="pdp-back">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-6 mt-3 grid md:grid-cols-2 gap-6">
        {/* Left: main image + thumbnail row */}
        <div>
          <div className="rounded-3xl bg-white border-2 border-[var(--bb-darkblue)]/10 relative aspect-[4/5] overflow-hidden">
            <img src={imgSrc(activeImage || product.image, 'main')} alt={product.name} className="w-full h-full object-contain p-4 transition-opacity duration-150" decoding="async" data-testid="pdp-main-image" />
            <div className="absolute top-3 left-3 flex flex-col gap-1.5">
              {(product.badges || []).map((b) => (
                <Badge key={b} className="rounded-full bg-white text-[var(--bb-darkblue)] text-[11px] font-nunito-black border border-[var(--bb-darkblue)]/10">{b}</Badge>
              ))}
            </div>
            {isPack && (
              <div className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[var(--bb-darkblue)] text-white text-xs font-nunito-black">
                <Package className="w-3.5 h-3.5" /> Pack of {product.pack_size} {product.pack_unit}{isMono ? ' · Mono' : ''}
              </div>
            )}
          </div>
          {thumbs.length > 1 && (
            <div className="mt-3 grid grid-cols-4 gap-2" data-testid="pdp-thumbnails">
              {thumbs.map((src) => (
                <button
                  key={src}
                  type="button"
                  onClick={() => setActiveImage(src)}
                  className={classNames('aspect-square rounded-2xl bg-white border-2 overflow-hidden p-1.5 transition', activeImage === src ? 'border-[var(--bb-darkblue)]' : 'border-[var(--bb-darkblue)]/10 hover:border-[var(--bb-darkblue)]/30')}
                  data-testid={`pdp-thumb-${src.slice(-12)}`}
                >
                  <img src={imgSrc(src, 'thumb')} alt="" className="w-full h-full object-contain" loading="lazy" decoding="async" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right: Details */}
        <div>
          <div className={classNames('text-xs uppercase tracking-wider font-nunito-black', tone.text)}>{product.category_label}</div>
          <h1 className="font-display text-3xl md:text-5xl text-[var(--bb-darkblue)] mt-1 leading-[0.95]" data-testid="product-title">{product.name}</h1>
          {product.pack_label && (
            <div
              className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-nunito-black text-[var(--bb-darkblue)] bg-[var(--bb-yellow-soft)] px-2.5 py-1 rounded-full"
              data-testid="pdp-pack-label"
            >
              {product.pack_label}
            </div>
          )}
          {product.tagline && <p className="text-sm md:text-base font-nunito-medium text-[var(--bb-darkgrey)] mt-2">{product.tagline}</p>}
          <div className="flex items-center gap-2 mt-3">
            <Star className="w-4 h-4 fill-[var(--bb-yellow)] text-[var(--bb-yellow)]" />
            <span className="text-sm font-nunito-black text-[var(--bb-darkblue)]">{product.rating?.toFixed?.(1) || '4.8'}</span>
            <span className="text-sm text-[var(--bb-darkgrey)] font-nunito-medium">· {product.reviews} reviews</span>
          </div>
          <div className="mt-4 flex items-baseline gap-3">
            <div className="font-display text-3xl text-[var(--bb-darkblue)]" data-testid="product-price">{inr(product.price)}</div>
            {discount > 0 && (<>
              <div className="text-sm text-[var(--bb-lightgrey)] line-through font-nunito-medium">{inr(product.compare_at_price)}</div>
              <Badge className="bg-[var(--bb-pink)] text-white rounded-full font-nunito-black">Save {discount}%</Badge>
            </>)}
          </div>

          {/* WHAT'S INSIDE — WHITE container with clickable chips */}
          {isPack && (product.pack_options?.length || 0) > 0 && (
            <div className="mt-4 rounded-2xl bg-white border-2 border-[var(--bb-darkblue)]/10 p-4">
              <div className="text-xs font-nunito-black uppercase tracking-wider text-[var(--bb-darkblue)]">What's inside</div>
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="pdp-pack-options">
                {(product.pack_options || []).map((o) => {
                  const active = activeImage === o.image;
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => o.image && setActiveImage(o.image)}
                      className={classNames(
                        'inline-flex items-center px-3 py-1 rounded-full text-xs font-nunito-bold border transition-colors',
                        active ? 'bg-[var(--bb-darkblue)] text-white border-[var(--bb-darkblue)]' : 'bg-white text-[var(--bb-darkblue)] border-[var(--bb-darkblue)]/15 hover:bg-[var(--bb-yellow-soft)]',
                      )}
                      data-testid={`pdp-chip-${o.id}`}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
              <div className="text-xs font-nunito-medium text-[var(--bb-darkgrey)] mt-2">
                {isMono ? `All ${product.pack_size} pieces are ${product.pack_options[0]?.label}.` : `Pick any combination of ${product.pack_size} in the customise step.`}
              </div>
            </div>
          )}

          <p className="text-sm font-nunito-medium text-[var(--bb-darkgrey)] mt-4 leading-relaxed">{product.description}</p>

          {!isPack && (
            <div className="mt-5 flex items-center gap-3">
              <div className="text-sm font-nunito-black text-[var(--bb-darkblue)]">Quantity</div>
              <div className="inline-flex items-center rounded-xl border-2 border-[var(--bb-darkblue)]/10 bg-white overflow-hidden">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-10 h-11 hover:bg-black/5" data-testid="qty-decrease"><Minus className="w-4 h-4 mx-auto" /></button>
                <div className="w-10 text-center text-sm font-nunito-black" data-testid="qty-value">{qty}</div>
                <button onClick={() => setQty((q) => q + 1)} className="w-10 h-11 hover:bg-black/5" data-testid="qty-increase"><Plus className="w-4 h-4 mx-auto" /></button>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="mt-6 flex flex-col sm:flex-row gap-2">
            {product.coming_soon ? (
              <Button
                disabled
                className="flex-1 h-12 rounded-xl font-nunito-black bg-[var(--bb-darkblue)] text-white opacity-90 cursor-not-allowed"
                data-testid="pdp-coming-soon-cta"
              >
                <Sparkles className="w-4 h-4 mr-1.5" /> Coming Soon · Launching this year
              </Button>
            ) : (
              <>
                <AddToCartButton
                  onAdd={addPlain}
                  variant={product.category === 'chocodigits' ? 'pink' : product.category === 'chocosnaps' ? 'yellow' : 'cyan'}
                  className="flex-1"
                  data-testid="add-to-cart-button"
                />
                {showCustomiseCTA ? (
                  <Button
                    onClick={goCustomise}
                    className="flex-1 h-12 rounded-xl font-nunito-black text-white bg-[var(--bb-pink)] hover:bg-[var(--bb-pink-deep)] inline-flex items-center justify-center gap-1.5 relative overflow-hidden"
                    data-testid="pdp-customise-cta"
                  >
                    <Sparkles className="w-4 h-4 bb-anim-wiggle" />
                    Customise this pack
                    <Sparkles className="w-3 h-3 opacity-80 bb-anim-wiggle" style={{ animationDelay: '0.3s' }} />
                  </Button>
                ) : (
                  <Button onClick={buyNow} variant="outline" className="flex-1 h-12 rounded-xl border-2 font-nunito-black" data-testid="buy-now-button">Buy Now</Button>
                )}
              </>
            )}
          </div>

          <div className="mt-6 grid grid-cols-2 gap-2">
            {[
              { Icon: Truck, label: 'Ships to select locations' },
              { Icon: ShieldCheck, label: 'Food-safe + edible inks' },
              { Icon: Heart, label: 'Made with love in India' },
              { Icon: Sparkles, label: 'Built to your spec' },
            ].map(({ Icon, label }) => (
              <div key={label} className="flex items-center gap-2 text-xs text-[var(--bb-darkgrey)] font-nunito-bold">
                <Icon className={classNames('w-4 h-4', tone.text)} /> {label}
              </div>
            ))}
          </div>

          <a href={`https://wa.me/${config.whatsapp_number}?text=Hi%20Big%20Bash%2C%20I%20have%20a%20question%20about%20${encodeURIComponent(product.name)}`} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm font-nunito-black text-[var(--bb-pink)] hover:underline" data-testid="pdp-whatsapp">
            <MessageCircle className="w-4 h-4" /> Have a question? WhatsApp us
          </a>
        </div>
      </div>

      {related.length > 0 && (
        <section ref={relatedRef} id="more-options" className="max-w-6xl mx-auto px-4 md:px-6 mt-12 scroll-mt-20">
          <h2 className="font-display text-2xl md:text-3xl text-[var(--bb-darkblue)] mb-3">Other packs you may like</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">{related.map((p) => <ProductCard key={p.slug} product={p} />)}</div>
        </section>
      )}

      <Footer />

      <div className="fixed left-0 right-0 z-40 bg-white border-t border-[var(--bb-darkblue)]/10 md:hidden" style={{ bottom: 'calc(64px + env(safe-area-inset-bottom))' }} data-testid="pdp-sticky-cta-bar">
        <div className="max-w-6xl mx-auto px-3 py-2.5 flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <div className="text-xs text-[var(--bb-darkgrey)] truncate font-nunito-bold">{product.name}</div>
            <div className="text-base font-display text-[var(--bb-darkblue)]">{inr(product.price * (isPack ? 1 : qty))}</div>
          </div>
          {product.coming_soon ? (
            <Button disabled className="h-11 px-4 rounded-xl font-nunito-black bg-[var(--bb-darkblue)] text-white opacity-90 cursor-not-allowed text-sm" data-testid="pdp-coming-soon-mobile">
              Coming Soon
            </Button>
          ) : (
            <>
              <AddToCartButton
                onAdd={addPlain}
                label="Add"
                successLabel="Added"
                variant={product.category === 'chocodigits' ? 'pink' : product.category === 'chocosnaps' ? 'yellow' : 'cyan'}
                className="h-11 px-4 text-sm"
                data-testid="add-to-cart-button-mobile"
              />
              {showCustomiseCTA && (
                <Button onClick={goCustomise} variant="outline" className="h-11 px-4 rounded-xl border-2 font-nunito-black" data-testid="pdp-customise-mobile">
                  <Sparkles className="w-4 h-4 mr-1" /> Customise
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
